import type {
  ICertificateDetails,
  ICertificateTemplate,
  ICertificateTermsTemplate,
  ICertificatePurchaseRequest,
  ICertificatePurchaseResult,
  ICertificatePaymentMethodUpdateRequest,
  ICertificatePaymentMethodUpdateResult,
  ICertificatePaymentMethodConfirm3DSRequest,
  IComplete3DSRequest,
  ICertificateTemplatesParams,
  ICertificateTemplatesResponse,
  IUserCertificatesResponse,
  IUserCertificateWithInstallments,
  ILandingPageRenderData,
  IPublicCertificatePaymentContext,
  IPublicCertificatePaymentLinkResponse,
  IPublicCertificatePaymentRequest,
  ICertificateItem,
} from '@share/common-types';
import { CertificateStatusEnum } from '@share/common-types';
import { axiosInstanceB2B, getHeaders, getPublicCertificatePaymentConfig, normalizeEligibleCruiseLines } from '@share/utils';
import { Urls } from '@share/constants';

const PUBLIC_PAYMENT_LINK_ITEM_EPOCH = new Date(0).toISOString();

const splitRecipientName = (
  recipientName: string,
): { recipientFirstName: string; recipientLastName: string } => {
  const trimmed = recipientName.trim();
  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return { recipientFirstName: trimmed, recipientLastName: '' };
  }

  return {
    recipientFirstName: trimmed.slice(0, spaceIndex),
    recipientLastName: trimmed.slice(spaceIndex + 1).trim(),
  };
};

const mapPublicPaymentLinkItemToCertificateItem = ({
  item,
  certificateId,
}: {
  item: IPublicCertificatePaymentLinkResponse['items'][number];
  certificateId: string;
}): ICertificateItem => ({
  id: '',
  type: item.type,
  name: item.type,
  nights: item.nights,
  internalPrice: 0,
  publicPrice: 0,
  createdAt: PUBLIC_PAYMENT_LINK_ITEM_EPOCH,
  certificateId,
  status: CertificateStatusEnum.Pending,
});

/** @internal Transitional mapper — Phase 6 moves to @share/utils */
const mapPublicPaymentLinkResponseToContext = (
  data: IPublicCertificatePaymentLinkResponse,
): IPublicCertificatePaymentContext => {
  const { recipientFirstName, recipientLastName } = splitRecipientName(data.recipientName);

  return {
    id: data.certificateId,
    name: data.certificateName,
    totalPrice: data.amount,
    recipientFirstName,
    recipientLastName,
    recipientEmail: data.recipientEmail,
    items: data.items.map((item) =>
      mapPublicPaymentLinkItemToCertificateItem({ item, certificateId: data.certificateId }),
    ),
    imageUrl: data.imageUrl,
    descriptionContent: null,
    eligibleCruiseLines: undefined,
    termsAndConditionsContent: undefined,
    currency: 'USD',
    clubSiteId: data.clubSiteId,
    validityMonths: data.validityMonths,
    expiresAt: data.expiresAt,
  };
};

const buildPublicCertificatePaymentPath = (
  token: string,
  suffix?: 'pay' | 'pay-3ds',
): string => {
  const encodedToken = encodeURIComponent(token);
  const base = `${Urls.PublicCertificatePaymentLink}/${encodedToken}`;

  if (suffix === 'pay') {
    return `${base}/pay`;
  }

  if (suffix === 'pay-3ds') {
    return `${base}/pay/3ds`;
  }

  return base;
};

const buildPublicCertificatePaymentMutationConfig = (idempotencyKey: string) => {
  const publicConfig = getPublicCertificatePaymentConfig();

  return {
    ...publicConfig,
    headers: {
      ...publicConfig.headers,
      'Idempotency-Key': idempotencyKey,
    },
  };
};

export const getCertificateTemplatesAsync = (
  params: ICertificateTemplatesParams,
): Promise<{ data: ICertificateTemplatesResponse }> => {
  const body = {
    search: params.search ?? '',
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
    sortBy: params.orderBy ?? 'createdAt',
    sortOrder: params.sortOrder ?? 'desc',
    itemTypes: params.itemTypes ?? [],
    isLive: true,
    clubSiteIds: params.clubSiteIds ?? [],
  };

  return axiosInstanceB2B
    .post(Urls.CertificateTemplatesSearch, body, { ...getHeaders() })
    .then((response) => ({
      ...response,
      data: {
        ...response.data,
        items: (response.data.items ?? []).map((item) => ({
          ...item,
          eligibleCruiseLines: normalizeEligibleCruiseLines(item.eligibleCruiseLines),
        })),
      },
    }));
};

export const getCertificateTemplateByIdAsync = (
  id: string,
  clubSiteId?: number,
): Promise<{ data: ICertificateTemplate }> =>
  axiosInstanceB2B
    .get(`${Urls.CertificateTemplateById}/${id}/details`, {
      ...getHeaders(),
      params: { clubSiteId },
    })
    .then((response) => ({
      ...response,
      data: {
        ...response.data,
        eligibleCruiseLines: normalizeEligibleCruiseLines(response.data.eligibleCruiseLines),
      },
    }));

export const getLandingPageByTemplateIdAsync = (
  templateId: string,
  clubSiteId?: number,
): Promise<{ data: ILandingPageRenderData }> =>
  axiosInstanceB2B.get(`${Urls.LandingPageByTemplateId}/${templateId}`, {
    ...getHeaders(),
    params: { clubSiteId },
  });

export const getCertificateByIdAsync = (
  id: string,
  clubSiteId?: number,
): Promise<{ data: ICertificateDetails }> =>
  axiosInstanceB2B
    .get(`${Urls.CertificateById}/${id}`, {
      ...getHeaders(),
      params: { clubSiteId },
    })
    .then((response) => ({
      ...response,
      data: {
        ...response.data,
        eligibleCruiseLines: normalizeEligibleCruiseLines(response.data.eligibleCruiseLines),
      },
    }));

export const getCertificateTermsTemplateByIdAsync = (
  id: string,
): Promise<{ data: ICertificateTermsTemplate }> =>
  axiosInstanceB2B.get(`${Urls.CertificateTermsTemplateById}/${id}`, {
    ...getHeaders(),
  });

export const purchaseCertificateAsync = (
  body: ICertificatePurchaseRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePurchaseResult }> =>
  axiosInstanceB2B.post(Urls.CertificatePurchase, body, {
    ...getHeaders(),
    headers: {
      ...getHeaders().headers,
      'Idempotency-Key': idempotencyKey,
    },
  });

export const complete3DSPurchaseAsync = (
  body: IComplete3DSRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePurchaseResult }> =>
  axiosInstanceB2B.post(Urls.CertificatePurchaseComplete3DS, body, {
    ...getHeaders(),
    headers: {
      ...getHeaders().headers,
      'Idempotency-Key': idempotencyKey,
    },
  });

export const updateCertificatePaymentMethodAsync = (
  certificateId: string,
  body: ICertificatePaymentMethodUpdateRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePaymentMethodUpdateResult }> =>
  axiosInstanceB2B.put(
    `${Urls.CertificateWithInstallmentsById}/${certificateId}/payment-method`,
    body,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        'Idempotency-Key': idempotencyKey,
      },
    },
  );

export const confirmCertificatePaymentMethod3DSAsync = (
  certificateId: string,
  body: ICertificatePaymentMethodConfirm3DSRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePaymentMethodUpdateResult }> =>
  axiosInstanceB2B.post(
    `${Urls.CertificateWithInstallmentsById}/${certificateId}/payment-method/confirm-3ds`,
    body,
    {
      ...getHeaders(),
      headers: {
        ...getHeaders().headers,
        'Idempotency-Key': idempotencyKey,
      },
    },
  );

interface IUserCertificatesPaging {
  pageNumber?: number;
  pageSize?: number;
}

interface IUserCertificatesParams {
  email?: string;
  organizationId?: number;
  paging?: IUserCertificatesPaging;
  statuses?: CertificateStatusEnum[];
}

const DEFAULT_USER_CERTIFICATE_STATUSES: CertificateStatusEnum[] = [
  CertificateStatusEnum.Active,
  CertificateStatusEnum.PartiallyRedeemed,
];

export const getCertificateWithInstallmentsById = (
  id: string,
  options?: { signal?: AbortSignal },
): Promise<{ data: IUserCertificateWithInstallments }> =>
  axiosInstanceB2B.get(`${Urls.CertificateWithInstallmentsById}/${id}`, {
    ...getHeaders(),
    signal: options?.signal,
  });

export const getUserCertificatesAsync = (
  params: IUserCertificatesParams = {},
): Promise<{ data: IUserCertificatesResponse }> =>
  axiosInstanceB2B.post(
    Urls.CertificatesByUser,
    {
      email: params.email ?? '',
      paging: {
        pageNumber: params.paging?.pageNumber ?? 1,
        pageSize: params.paging?.pageSize ?? 100,
      },
      statuses: params.statuses ?? DEFAULT_USER_CERTIFICATE_STATUSES,
      clubSiteIds:
        params.organizationId !== undefined && params.organizationId !== null
          ? [params.organizationId]
          : [],
    },
    { ...getHeaders() },
  );

export const getPublicCertificatePaymentContextAsync = async (
  token: string,
  options?: { signal?: AbortSignal },
): Promise<{ data: IPublicCertificatePaymentContext }> => {
  const response = await axiosInstanceB2B.get<IPublicCertificatePaymentLinkResponse>(
    buildPublicCertificatePaymentPath(token),
    {
      ...getPublicCertificatePaymentConfig(),
      signal: options?.signal,
    },
  );

  return { data: mapPublicPaymentLinkResponseToContext(response.data) };
};

export const submitPublicCertificatePaymentAsync = (
  token: string,
  body: IPublicCertificatePaymentRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePurchaseResult }> =>
  axiosInstanceB2B.post(
    buildPublicCertificatePaymentPath(token, 'pay'),
    body,
    buildPublicCertificatePaymentMutationConfig(idempotencyKey),
  );

export const completePublicCertificatePayment3DSAsync = (
  token: string,
  body: IComplete3DSRequest,
  idempotencyKey: string,
): Promise<{ data: ICertificatePurchaseResult }> =>
  axiosInstanceB2B.post(
    buildPublicCertificatePaymentPath(token, 'pay-3ds'),
    body,
    buildPublicCertificatePaymentMutationConfig(idempotencyKey),
  );
